import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Base64 } from "js-base64";

import { RevisionFileAction } from "types/generated";
import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import CodeEditor from "components/CodeEditor";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Button from "ds/components/Button";
import useErrorHandle from "hooks/useErrorHandle";
import useGetSpaceliftVcsRepo from "shared/SpaceliftVcsRepo/useGetSpaceliftVcsRepo";
import { getRepoUrl, REPOS_PAGE_URL } from "utils/spaceliftRepo";

import {
  buildFolderBreadcrumbs,
  buildPathSearch,
  parseFilePath,
  SPACELIFT_VCS_REPO_SORT_STORAGE_KEY,
} from "../helpers";
import useFilesNavigation from "../Files/Table/useFilesNavigation";
import SpaceliftVcsRepoCommitDialog, { CommitArgs } from "../CommitDialog";
import useRepoCommit from "../useRepoCommit";

const SpaceliftVcsRepoCreateFile = () => {
  const { repoId } = useParams<{ repoId: string }>();

  const navigate = useNavigate();
  const { folderPathSegments, folderPath } = useFilesNavigation();

  const { repo, loading: repoLoading, error: repoError } = useGetSpaceliftVcsRepo({ repoId });
  const { commit, loading: commitLoading } = useRepoCommit();

  const [content, setContent] = useState("");
  const [isCommitDialogOpen, setIsCommitDialogOpen] = useState(false);

  const repoUrl = getRepoUrl(repoId!);

  useBreadcrumbs(
    [
      {
        title: "Repos",
        link: getBreadcrumbsBackUrl(REPOS_PAGE_URL, SPACELIFT_VCS_REPO_SORT_STORAGE_KEY),
      },
      {
        title: repo?.name || "",
        link: repoUrl,
      },
      ...buildFolderBreadcrumbs(repoUrl, folderPathSegments),
      {
        title: "New file",
      },
    ],
    [repo?.name, repoUrl, folderPathSegments.length]
  );

  const handleCancel = () => {
    navigate(`${repoUrl}${buildPathSearch(folderPath)}`);
  };

  const handleCommit = ({
    filePath: newPath = "",
    commitMessage,
    commitDescription,
  }: CommitArgs) => {
    commit(
      [
        {
          path: newPath,
          content: Base64.encode(content),
          encrypt: null,
          fileMode: null,
          action: RevisionFileAction.Create,
        },
      ],
      commitMessage,
      commitDescription,
      () => {
        setIsCommitDialogOpen(false);
        const { folder: newFolder } = parseFilePath(newPath);
        const search = buildPathSearch(newFolder);
        navigate(`${repoUrl}${search}`);
      }
    );
  };

  const ErrorContent = useErrorHandle(repoError);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (repoLoading && !repo) {
    return <PageLoading />;
  }

  if (!repo) {
    return <NotFoundPage />;
  }

  return (
    <>
      <ViewHeader>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>New file</ViewHeaderTitle>
          </ViewHeaderWrapper>

          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <Button variant="secondary" onPress={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={() => setIsCommitDialogOpen(true)}
              disabled={!content}
            >
              Commit changes
            </Button>
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>
      </ViewHeader>

      <CodeEditor
        body={content}
        onChange={(value) => setContent(value || "")}
        language={"plaintext"}
      />

      <SpaceliftVcsRepoCommitDialog
        variant="create"
        isOpen={isCommitDialogOpen}
        onClose={() => setIsCommitDialogOpen(false)}
        onCommit={handleCommit}
        loading={commitLoading}
      />
    </>
  );
};

export default SpaceliftVcsRepoCreateFile;
